import React, { useState, useEffect } from 'react';
import { gql, useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { Button, Form, Select, Skeleton, Switch, notification, AutoComplete } from 'antd';
import { useIsAuthenticated } from '@azure/msal-react';
import CustomInput from './CustomComponents/CustomInput';
import {
  ADD_NEW_RESOURCE,
  GET_RESOURCE,
  UPDATE_RESOURCE,
  SEARCH_MEMBERS,
  SEARCH_EMPLOYEE,
  typeOptions,
} from './ResourceConstants';

interface ResourceFormProps {
  onCancel: () => void;
  proceedingId: string;
  resourceId: string;
  onSuccess: () => void;
  form: any;
}

const ResourceForm: React.FC<ResourceFormProps> = ({
  onCancel,
  proceedingId,
  resourceId,
  onSuccess,
  form,
}) => {
  const phoneRegex = /^(\+\d{1,2}\s?)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/;
  const [addNewResource, { loading: adding, error: addError, data: addData }] =
    useMutation(ADD_NEW_RESOURCE);
  const [getResourceDetails, { data: resourceDetails, loading: readingResourceDetails, refetch }] =
    useLazyQuery(GET_RESOURCE);
  const [updateResource, { loading: updating, error: updateError, data: updateData }] =
    useMutation(UPDATE_RESOURCE);

  const [isInternal, setIsInternal] = useState('yes');
  const [isChiefCHC, setIsChiefCHC] = useState('no');
  const [isDirty, setIsDirty] = useState(false);
  const [type, setType] = useState<string | null>(null);
  const isAuthenticated = useIsAuthenticated();

  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [selectedMember, setSelectedMember] = useState<any | null>(null);
  const [isValidMemberSelected, setIsValidMemberSelected] = useState(false);

  const {
    data: searchData,
    loading: searchLoading,
    error: searchError,
  } = useQuery(SEARCH_MEMBERS, { variables: { input: '' }, skip: !isAuthenticated });

  const [searchEmployee, { data: searchEmployeeData, loading: searchEmployeeLoading }] =
    useLazyQuery(SEARCH_EMPLOYEE);

  useEffect(() => {
    if (proceedingId && resourceId) {
      getResourceDetails({
        variables: {
          input: {
            proceedingNumber: proceedingId,
            resourceId: resourceId,
          },
        },
      });
    }

    if (proceedingId && !resourceId) {
      setType('');
    }
  }, [proceedingId, resourceId]);

  useEffect(() => {
    if (resourceDetails?.resource) {
      const formValues = resourceDetails.resource;
      form.setFieldsValue(formValues);
      setType(formValues.type);
      setIsInternal(formValues.isInternal);
      setIsChiefCHC(formValues.isChiefChc);
      setIsDirty(false);

      setIsValidMemberSelected(true);
    }

    if (searchData?.searchMembers) {
      setSearchResults(searchData.searchMembers);
      if (resourceDetails?.resource) {
        const member = searchData.searchMembers.find(
          (member: any) => member.id === resourceDetails.resource.employeeId
        );
        if (member) {
          setSelectedMember(member);
          setMemberFields(member);
        } else {
          setSelectedMember(null);
          form.setFieldsValue({
            isInternal: resourceDetails.resource.isInternal,
            search: resourceDetails.resource.firstName + ' ' + resourceDetails.resource.lastName,
            firstName: resourceDetails.resource.firstName,
            lastName: resourceDetails.resource.lastName,
            phoneNumber: resourceDetails.resource.phoneNumber.replace(/\D/g, '').slice(0, 10),
            email: resourceDetails.resource.email,
          });
        }
      }
    }
  }, [resourceDetails, searchData]);

  useEffect(() => {
    setSearchResults(searchEmployeeData?.searchEmployee);
  }, [searchEmployeeData]);

  const setMemberFields = (member: any) => {
    const formattedPhoneNumber = member.phoneNumber.replace(/\D/g, '').slice(0, 10);
    form.setFieldsValue({
      search: member.firstName + ' ' + member.lastName,
      firstName: member.firstName,
      lastName: member.lastName,
      phoneNumber: formattedPhoneNumber,
      email: member.email,
    });

    if (type === 'Hearing Coordinator') {
      form.setFieldsValue({
        search: member.id,
      });
    } else {
      form.setFieldsValue({
        search: { value: member.displayName },
      });
    }
    setIsValidMemberSelected(true);
  };

  const setAsInternal = (checked: boolean) => {
    setIsInternal(checked ? 'no' : 'yes');
    form.setFieldsValue({
      firstName: '',
      lastName: '',
      phoneNumber: '',
      email: '',
      search: '',
    });
    setIsDirty(true);
  };

  const setIfChiefCHC = (checked: boolean) => {
    setIsChiefCHC(checked ? 'yes' : 'no');
    setIsDirty(true);
  };

  const handleSearchEmployee = (value: string) => {
    setIsValidMemberSelected(false);
    if (value.length >= 4) {
      searchEmployee({ variables: { input: value } })
        .then((response) => {
          const validResults = response.data.searchEmployee.filter((user: any) => user.id);
          setSearchResults(validResults);
        })
        .catch((error) => {
          notification.warning({
            message: 'No Employee Found',
          });
        });
    } else {
      setSearchResults([]);
    }
  };

  const handleSelectEmployee = (value: string, option: any) => {
    const member = searchResults.find((user: any) => user.id === value);

    if (member) {
      setSelectedMember(member);
      form.setFieldsValue({ search: member.displayName });
      setMemberFields(member);
      setIsValidMemberSelected(true);
    } else {
      setIsValidMemberSelected(false);
    }
  };

  const handleSelectMember = (value: string) => {
    const member = searchResults.find((member: any) => member.id === value);
    if (member) {
      setSelectedMember(member);
      setMemberFields(member);
      setIsValidMemberSelected(true);
    } else {
      setIsValidMemberSelected(false);
    }
  };

  const handleTypeChange = (value: string) => {
    setType(value);
    form.resetFields([
      'firstName',
      'lastName',
      'phoneNumber',
      'email',
      'company',
      'discipline',
      'isInternal',
      'isChiefCHC',
      'search',
    ]);
    setSelectedMember(null);

    if (value === 'Hearing Coordinator') {
      if (searchData?.searchMembers) {
        setSearchResults(searchData.searchMembers);
      }
    } else {
      setIsInternal('yes');
      setSearchResults([]);
    }

    setIsDirty(true);
  };

  const onFinish = async (values: any) => {
    if (!isValidMemberSelected && isInternal === 'yes') {
      notification.error({
        message: 'Error',
        description: 'Please select a valid team member from the search results',
      });
      return;
    }
    try {
      const input = {
        id: resourceId,
        proceedingNumber: proceedingId,
        type: values.type,
        isInternal: isInternal,
        company: values.company,
        discipline: values.discipline,
        employeeId: selectedMember?.id,
        isChiefChc: isChiefCHC,
        lastName: selectedMember ? selectedMember.lastName : values.lastName,
        firstName: selectedMember ? selectedMember.firstName : values.firstName,
        email: selectedMember ? selectedMember.email : values.email,
        phoneNumber: selectedMember ? selectedMember.phoneNumber : values.phoneNumber,
      };
      if (resourceId) {
        const { data, errors } = await updateResource({ variables: { input } });

        if (errors || data.updateResource.result !== 'Success') {
          notification.error({
            message: 'Error',
            description: errors?.[0]?.message || 'Failed to update resource',
          });
          return;
        }

        notification.success({
          message: 'Success',
          description: 'Resource updated successfully',
        });
      } else {
        const { data, errors } = await addNewResource({ variables: { input } });

        if (errors || data.addResource.result !== 'Success') {
          notification.error({
            message: 'Error',
            description: errors?.[0]?.message || 'Failed to add resource',
          });
          return;
        }

        notification.success({
          message: 'Success',
          description: 'Resource added successfully',
        });
      }
      onSuccess();
      onCancel();
    } catch (error) {
      notification.error({
        message: 'Error',
        description: 'Error in saving resource',
      });
    }
  };
  return (
    <>
      {(readingResourceDetails || updating) && <Skeleton />}
      {!(readingResourceDetails || updating) && (
         <div className="thin-border">
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          onValuesChange={() => setIsDirty(true)}
        >
          <Form.Item
            name="type"
            label="Type of Resource"
            rules={[{ required: true, message: 'Please select the type' }]}
          >
            <Select
              options={typeOptions}
              disabled={!isAuthenticated}
              onChange={handleTypeChange}
              data-testid="createResourceSelectType"
            />
          </Form.Item>
          {type === 'Hearing Coordinator' && (
            <>
              <Form.Item
                name="search"
                label="Team Member"
                rules={[{ required: true, message: 'Please select the type' }]}
                data-testid="TeamMemberLabel"
              >
                <Select
                  showSearch
                  onSelect={handleSelectMember}
                  disabled={!isAuthenticated}
                  onClear={() => {
                    setSelectedMember(null);
                    form.resetFields(['firstName', 'lastName', 'phoneNumber', 'email']);
                  }}
                  filterOption={false}
                  notFoundContent={searchLoading && <Skeleton />}
                  data-testid="TeamMember"
                >
                  {(searchResults || [])
                    .filter((member: any) => member)
                    .map((member: any) => (
                      <Select.Option key={member.id} value={member.id}>
                        {member.displayName}
                      </Select.Option>
                    ))}
                </Select>
              </Form.Item>
            </>
          )}
          {type && type !== 'Hearing Coordinator' && (
            <>
              {/*  This field started with Internal or not. Now changes to External hence only lebal changed and values still refers to isInternal */}
              <Form.Item name="isInternal" label="External">
                <Switch
                  disabled={!isAuthenticated}
                  onChange={setAsInternal}
                  checked={isInternal !== 'yes'}
                />
              </Form.Item>
              {isInternal === 'yes' && (
                <Form.Item
                  name="search"
                  label="Search Employee"
                  rules={[{ required: true, message: 'Please select an employee' }]}
                >
                  <AutoComplete
                    onSearch={handleSearchEmployee}
                    onSelect={handleSelectEmployee}
                    disabled={!isAuthenticated}
                    options={(searchResults || [])
                      .filter((user: any) => user)
                      .map((user: any) => ({
                        value: user.id,
                        label: user.displayName,
                        key: user.id,
                      }))}
                    notFoundContent={searchEmployeeLoading && <Skeleton />}
                    data-testid="searchEmployee"
                  />
                </Form.Item>
              )}
            </>
          )}

          <Form.Item
            name="firstName"
            label="First Name"
            rules={[
              {
                required: isInternal === 'no' && type !== 'Hearing Coordinator',
                message: 'Please enter the first name.',
              },
            ]}
          >
            <CustomInput
              showInput={true}
              disabled={!isAuthenticated || isInternal === 'yes' || type === 'Hearing Coordinator'}
              data-testid="firstName"
            ></CustomInput>
          </Form.Item>
          <Form.Item
            name="lastName"
            label="Last Name"
            rules={[
              {
                required: isInternal === 'no' && !(type === 'Hearing Coordinator'),
                message: 'Please enter the last name',
              },
            ]}
          >
            <CustomInput
              showInput={true}
              disabled={!isAuthenticated || isInternal === 'yes' || type === 'Hearing Coordinator'}
              data-testid="lastName"
            ></CustomInput>
          </Form.Item>
          <Form.Item
            name="phoneNumber"
            label="Phone #"
            rules={[
              {
                required: isInternal === 'no' && type !== 'Hearing Coordinator',
                pattern: phoneRegex,
                message: 'Please enter the phone #.',
              },
              { max: 10, message: 'Phone number cannot be longer than 10 digits.' },
            ]}
          >
            <CustomInput
              showInput={true}
              disabled={!isAuthenticated || isInternal === 'yes' || type === 'Hearing Coordinator'}
              maxLength={10}
              data-testid="phoneNumber"
            ></CustomInput>
          </Form.Item>
          <Form.Item
            name="email"
            label="Email"
            rules={[
              {
                required: isInternal === 'no' && type !== 'Hearing Coordinator',
                type: 'email',
                message: 'Please enter the email address.',
              },
            ]}
          >
            <CustomInput
              showInput={true}
              disabled={!isAuthenticated || isInternal === 'yes' || type === 'Hearing Coordinator'}
              data-testid="email"
            ></CustomInput>
          </Form.Item>
          {(type === 'Hearing Commissioner' || type === 'Hearing Commissioner - ADR') && (
            <Form.Item name="isChiefCHC" label="Chair">
              <Switch
                disabled={!isAuthenticated}
                onChange={setIfChiefCHC}
                checked={isChiefCHC === 'yes'}
              />
            </Form.Item>
          )}

          {type !== 'Hearing Coordinator' && isInternal === 'no' && (
            <>
              <Form.Item name="company" label="Company">
                <CustomInput
                  showInput={true}
                  disabled={!isAuthenticated}
                  data-testid="company"
                ></CustomInput>
              </Form.Item>
              <Form.Item name="discipline" label="Discipline">
                <CustomInput
                  showInput={true}
                  disabled={!isAuthenticated}
                  data-testid="discipline"
                ></CustomInput>
              </Form.Item>
            </>
          )}
          {isAuthenticated && (
            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                data-testid="createResourceBtn"
                disabled={!isDirty}
                loading={adding || updating}
              >
                Submit
              </Button>
              <Button
                onClick={onCancel}
                data-testid="createResourceCancelBtn"
                style={{ marginLeft: 8 }}
              >
                Cancel
              </Button>
            </Form.Item>
          )}
          {!isAuthenticated && (
            <Button
              type="primary"
              onClick={onCancel}
              data-testid="createResourceBackBtn"
              style={{ marginLeft: 8 }}
            >
              Back
            </Button>
          )}
        </Form>
      </div>
      )}
    </>
  );
};

export default ResourceForm;
